import React, { useState, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  MagnifyingGlass,
  Funnel,
  Check,
  Buildings,
  CaretLeft,
  CaretRight,
  User,
  ShieldCheck,
  Phone,
  Envelope,
  CalendarCheck,
  Info,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";

import { useGetSecurityDashboardData } from "../../../adapters/Requests";

export default function SecurityTable() {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);

  const { data, isLoading, isFetching } = useGetSecurityDashboardData({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: searchQuery,
    status: selectedStatus,
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setSearchQuery(searchTerm);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const uniqueStatuses = ["Active", "Pending", "Done"];

  const InternDetailsModal = ({ intern, children }) => (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="w-[95%] max-w-md rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-800">
            <ShieldCheck size={24} className="text-green-600" />
            Intern Profile
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 max-h-[75vh] overflow-y-auto pr-1">
          <div className="bg-slate-50 border border-slate-100 rounded-lg p-4 flex items-center gap-4 mb-4">
            {intern.photo ? (
              <img
                src={intern.photo}
                className="w-16 h-16 rounded-full border-2 border-white shadow-sm object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center text-slate-400">
                <User size={32} />
              </div>
            )}
            <div>
              <h3 className="font-bold text-lg text-slate-900 leading-tight">
                {intern.name}
              </h3>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs font-mono bg-white border border-slate-200 px-2 py-0.5 rounded text-slate-500">
                  {intern.code}
                </span>
                <Badge
                  variant={intern.status === "Active" ? "success" : "outline"}
                  className="text-[10px] px-2"
                >
                  {intern.status}
                </Badge>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-slate-400 uppercase">
                Contact
              </h4>
              <div className="grid gap-2">
                <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 p-2 rounded border border-slate-100">
                  <Envelope size={16} className="text-slate-400" />
                  <span className="truncate">{intern.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 p-2 rounded border border-slate-100">
                  <Phone size={16} className="text-slate-400" />
                  <span>{intern.phone || "No Phone"}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-bold text-slate-400 uppercase">
                Placement
              </h4>
              <div className="border border-slate-100 rounded-md p-3 text-sm">
                <div className="mb-2">
                  <span className="block text-xs text-slate-400">
                    Department
                  </span>
                  <div className="font-medium text-slate-800 flex items-center gap-1">
                    <Buildings size={14} className="text-green-600" />{" "}
                    {intern.department}
                  </div>
                </div>
                <div className="border-t border-slate-100 pt-2">
                  <span className="block text-xs text-slate-400">
                    Supervisor
                  </span>
                  <div className="font-medium text-slate-800">
                    {intern.supervisor}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-100 rounded-md p-3 flex justify-between items-center text-center">
              <div>
                <div className="text-[10px] text-slate-500 font-bold">
                  Start Date
                </div>
                <div className="text-sm font-bold text-green-700">
                  {intern.start_date}
                </div>
              </div>
              <CalendarCheck size={20} className="text-green-300" />
              <div>
                <div className="text-[10px] text-slate-500 font-bold">
                  End Date
                </div>
                <div className="text-sm font-bold text-red-600">
                  {intern.end_date}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  const MobileCard = ({ row }) => {
    const intern = row.original;
    const isActive = intern.status === "Active";

    return (
      <InternDetailsModal intern={intern}>
        <div
          className={`
            bg-white p-3 rounded-lg border shadow-sm cursor-pointer active:scale-95 transition-all
            flex flex-col items-center text-center gap-2 relative overflow-hidden
            ${isActive ? "border-green-200 shadow-green-50" : "border-slate-200"
            }
        `}
        >
          <div
            className={`absolute top-2 right-2 w-2 h-2 rounded-full ${isActive ? "bg-green-500" : "bg-slate-300"
              }`}
          ></div>

          {intern.photo ? (
            <img
              src={intern.photo}
              className="w-14 h-14 rounded-full object-cover border border-slate-100"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <User size={24} />
            </div>
          )}

          <div className="w-full">
            <h4 className="font-bold text-slate-800 text-xs truncate w-full px-1 leading-tight">
              {intern.name}
            </h4>
            <span className="text-[10px] font-mono text-slate-400 block mt-0.5">
              {intern.code || "NO ID"}
            </span>
          </div>

          <div className="text-[10px] text-slate-500 bg-slate-50 px-2 py-1 rounded w-full truncate">
            {intern.department}
          </div>
        </div>
      </InternDetailsModal>
    );
  };

  const columns = [
    {
      header: "Intern",
      accessorKey: "name",
      cell: ({ row }) => (
        <div className="flex items-center gap-3 py-1">
          {row.original.photo ? (
            <img
              src={row.original.photo}
              className="w-9 h-9 rounded-full object-cover border border-slate-200"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <User size={18} />
            </div>
          )}
          <div className="flex flex-col">
            <span className="font-bold text-slate-800 text-sm">
              {row.original.name}
            </span>
            <span className="text-[10px] text-slate-500 font-mono">
              {row.original.code}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Dept / Sup",
      accessorKey: "department",
      cell: ({ row }) => (
        <div className="flex flex-col max-w-[150px]">
          <span className="text-xs font-medium text-slate-700 truncate">
            {row.original.department}
          </span>
          <span className="text-[10px] text-slate-500 truncate">
            {row.original.supervisor}
          </span>
        </div>
      ),
    },
    {
      header: "Duration",
      accessorKey: "duration",
      cell: ({ row }) => (
        <div className="text-[10px] text-slate-600">
          <div>
            <span className="text-green-700 font-medium">In:</span>{" "}
            {row.original.start_date}
          </div>
          <div>
            <span className="text-red-700 font-medium">Out:</span>{" "}
            {row.original.end_date}
          </div>
        </div>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => {
        let variant = "outline";
        if (row.original.status === "Active") variant = "success";
        else if (row.original.status === "Pending") variant = "warning";
        return (
          <Badge variant={variant} className="text-[10px] px-2">
            {row.original.status}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <InternDetailsModal intern={row.original}>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 rounded-full hover:bg-slate-100"
          >
            <Info size={18} className="text-slate-500" />
          </Button>
        </InternDetailsModal>
      ),
    },
  ];

  const tableData = useMemo(() => data?.interns || [], [data]);
  const pageCount = data?.totalPages || -1;

  const table = useReactTable({
    data: tableData,
    columns,
    pageCount,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  return (
    <Card className="w-full border-slate-200 shadow-sm bg-slate-50/50 sm:bg-white mt-4">
      <CardHeader className="flex flex-col gap-4 p-4 border-b border-slate-100 bg-white sm:bg-gray-50/40 sticky top-0 z-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 self-start sm:self-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <ShieldCheck size={20} className="text-green-700" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-800">
                Security Checkpoint
              </h3>
              <p className="text-[11px] text-gray-500">
                Entry & Exit Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <MagnifyingGlass
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={14}
              />
              <Input
                placeholder="Search intern..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearch}
                className="pl-9 h-9 text-xs bg-white"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-3 bg-white"
                >
                  <Funnel className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedStatus(null);
                    setPagination((p) => ({ ...p, pageIndex: 0 }));
                  }}
                >
                  {!selectedStatus && <Check className="mr-2 h-3 w-3" />} All
                </DropdownMenuItem>
                {uniqueStatuses.map((status) => (
                  <DropdownMenuItem
                    key={status}
                    onClick={() => {
                      setSelectedStatus(status);
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                  >
                    {selectedStatus === status && (
                      <Check className="mr-2 h-3 w-3" />
                    )}{" "}
                    {status}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0 relative">
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
          </div>
        )}
        <div className="block md:hidden p-4">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin text-green-600" />
            </div>
          ) : table.getRowModel().rows.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              No interns found.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {table.getRowModel().rows.map((row) => (
                <MobileCard key={row.id} row={row} />
              ))}
            </div>
          )}
        </div>

        <div className="hidden md:block overflow-x-auto min-h-[300px]">
          <Table>
            <TableHeader className="bg-gray-50/50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="hover:bg-transparent border-b border-slate-100"
                >
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="h-9 text-[11px] font-bold text-slate-500 uppercase tracking-wider"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-64 text-center"
                  >
                    <Loader2 className="w-8 h-8 text-green-500 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="hover:bg-slate-50 border-b border-slate-100"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-2">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-32 text-center text-slate-500 text-xs"
                  >
                    No interns found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <div className="flex items-center justify-between p-3 border-t border-slate-100 bg-white sm:bg-gray-50/30 sticky bottom-0 z-10">
        <div className="text-[10px] text-slate-500">
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount() < 1 ? 1 : table.getPageCount()}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-7 px-2 bg-white text-[10px]"
          >
            <CaretLeft className="mr-1" size={10} /> Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-7 px-2 bg-white text-[10px]"
          >
            Next <CaretRight className="ml-1" size={10} />
          </Button>
        </div>
      </div>
    </Card>
  );
}
